import { useEffect, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "src/slices/store";
import { fetchTabData } from "src/slices/TabData/thunk";
import {
  buildCacheKey,
  emptyTabDataEntry,
  setTabView,
  clearTabData,
} from "src/slices/TabData/reducer";
import { TabDefinition, TabRequestContext } from "src/pages/ReportsPage/tabRegistry";

interface UsePaginatedTabDataArguments {
  tabDefinition:  TabDefinition;
  exchangeId:     string | number;
  requestContext: TabRequestContext;
  /** Only fetch while this tab is the active one. */
  isActive:       boolean;
}

export function usePaginatedTabData({
  tabDefinition,
  exchangeId,
  requestContext,
  isActive,
}: UsePaginatedTabDataArguments) {
  const dispatch = useDispatch();

  const cacheKey = buildCacheKey(exchangeId, tabDefinition.tabId);
  const cacheEntry =
    useSelector((state: RootState) => state.tabData.entries[cacheKey]) ?? emptyTabDataEntry;

  // All metrics deselected → no data to show or fetch. Distinct from undefined
  // (not yet loaded) where we still need the initial fetch to discover permitted metrics.
  const allMetricsDeselected =
    cacheEntry.permittedMetrics.length > 0 && cacheEntry.selectedMetrics.length === 0;

  // Effective view-state — persisted in Redux so it survives this tab's unmount.
  // Sort falls back to the registry default until an entry has been seeded (sortColumn === '').
  const currentPage   = cacheEntry.currentPage;
  const perPage       = cacheEntry.perPage;
  const sortColumn    = cacheEntry.sortColumn || tabDefinition.defaultSortColumn;
  const sortDirection = cacheEntry.sortColumn ? cacheEntry.sortDirection : tabDefinition.defaultSortDirection;

  /** Assemble the full param set: query identity + pagination + sort. */
  const assembleRequestParams = useCallback(
    (page: number, size: number, column: string, direction: "asc" | "desc") => ({
      ...tabDefinition.buildRequestParams(requestContext),
      page,
      per_page:   size,
      sort_by:    column,
      sort_order: direction,
    }),
    [tabDefinition, requestContext]
  );

  // Query identity = the filter-driven params only (order/dates/lineItems/dataBy),
  // excluding page/sort. A change here means the cached data is stale.
  const queryIdentity = useMemo(
    () => JSON.stringify(tabDefinition.buildRequestParams(requestContext)),
    [tabDefinition, requestContext]
  );

  /** Run a fetch unless the cache already holds this exact query. */
  const loadData = useCallback(
    (page: number, size: number, column: string, direction: "asc" | "desc") => {
      if (!tabDefinition.endpoint) return; // main tab has no endpoint

      const requestParams = assembleRequestParams(page, size, column, direction);
      const fetchKey = JSON.stringify({ cacheKey, ...requestParams });

      // Cache hit — identical query already in the store. Render instantly, no fetch.
      if (cacheEntry.fetchKey === fetchKey) return;

      dispatch(
        fetchTabData({
          cacheKey,
          endpoint: tabDefinition.endpoint,
          requestParams,
          fetchKey,
          queryIdentity,
        }) as any
      );
    },
    [tabDefinition, assembleRequestParams, cacheKey, cacheEntry.fetchKey, queryIdentity, dispatch]
  );

  // Reconcile on activation or when the query identity changes.
  useEffect(() => {
    if (!isActive) return;

    const notReady =
      !requestContext.orderId || !requestContext.startDate || !requestContext.endDate;
    if (notReady) return;

    // Explicitly empty selection → clear the table (distinct from not-ready).
    if (requestContext.selectedLineItems.length === 0) {
      dispatch(clearTabData({ cacheKey }));
      return;
    }

    // All metrics deselected → skip fetch, keep showing "No data" via visibleColumns=[].
    if (allMetricsDeselected) return;

    // Cached data was produced by a different filter set → stale → reset to page 1.
    const identityChanged =
      cacheEntry.queryIdentity !== "" && cacheEntry.queryIdentity !== queryIdentity;

    const pageToLoad = identityChanged ? 1 : currentPage;
    if (identityChanged && currentPage !== 1) {
      dispatch(setTabView({ cacheKey, currentPage: 1 }));
    }

    loadData(pageToLoad, perPage, sortColumn, sortDirection);
  }, [isActive, queryIdentity, cacheKey, allMetricsDeselected]);

  const handleSortChange = useCallback(
    (columnId: string) => {
      const nextDirection: "asc" | "desc" =
        sortColumn === columnId && sortDirection === "asc" ? "desc" : "asc";
      dispatch(setTabView({ cacheKey, sortColumn: columnId, sortDirection: nextDirection, currentPage: 1 }));
      if (!allMetricsDeselected) loadData(1, perPage, columnId, nextDirection);
    },
    [sortColumn, sortDirection, perPage, cacheKey, dispatch, loadData, allMetricsDeselected]
  );

  const handlePageChange = useCallback(
    (nextPage: number) => {
      dispatch(setTabView({ cacheKey, currentPage: nextPage }));
      if (!allMetricsDeselected) loadData(nextPage, perPage, sortColumn, sortDirection);
    },
    [perPage, sortColumn, sortDirection, cacheKey, dispatch, loadData, allMetricsDeselected]
  );

  const handlePerPageChange = useCallback(
    (nextPerPage: number) => {
      dispatch(setTabView({ cacheKey, perPage: nextPerPage, currentPage: 1 }));
      if (!allMetricsDeselected) loadData(1, nextPerPage, sortColumn, sortDirection);
    },
    [sortColumn, sortDirection, cacheKey, dispatch, loadData, allMetricsDeselected]
  );

  return {
    rows:            cacheEntry.rows,
    isLoading:       cacheEntry.isLoading,
    errorMessage:    cacheEntry.errorMessage,
    totalRecords:    cacheEntry.pagination.totalRecords,
    totalPages:      cacheEntry.pagination.totalPages,
    currentPage,
    perPage,
    sortColumn,
    sortDirection,
    onSortChange:    handleSortChange,
    onPageChange:    handlePageChange,
    onPerPageChange: handlePerPageChange,
  };
}
